import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styled from "styled-components";

function hexToRgb(hex) {
	const n = parseInt(hex.slice(1), 16);
	return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Full-section cinematic engine for the business-division showcase.
 * A single always-mounted canvas covers the whole section and runs a
 * three-act loop per unit:
 *   1. the current scene dissolves as an energy wave, born at the selector
 *      button that triggered the change, sweeps across the section;
 *   2. the wavefront carries sparks and a color flash of the next unit;
 *   3. the next scene builds up in the wave's wake, anchored to the disc.
 * Each unit's idle scene owns the ENTIRE section: the accelerator fires
 * stray beams across it, liftoff sinks a starfield (ascent illusion) and
 * launches comets, expansion colonizes the far edges with glow footholds.
 */
const DivisionCinema = forwardRef(function DivisionCinema(
	{ initialMode, initialAccent },
	apiRef,
) {
	const canvasRef = useRef(null);
	const engineRef = useRef(null);

	useImperativeHandle(apiRef, () => ({
		getRect: () =>
			canvasRef.current ? canvasRef.current.getBoundingClientRect() : null,
		setAnchor: (x, y, r) => {
			const eng = engineRef.current;
			if (eng) eng.setAnchor(x, y, r);
		},
		transition: (next) => {
			const eng = engineRef.current;
			if (eng) eng.transition(next);
		},
	}));

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return undefined;
		const ctx = canvas.getContext("2d");

		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const dpr = Math.min(window.devicePixelRatio || 1, 2);

		let width = 0;
		let height = 0;
		let rafId = null;
		let running = false;
		let inView = true;
		let frame = 0;

		// scene state -------------------------------------------------------
		let mode = initialMode;
		let color = hexToRgb(initialAccent);
		const cur = [...color]; // lerped display color
		const anchor = { x: 0, y: 0, R: 130 };
		let wave = null; // {x, y, r, cover, color, flash}
		let pending = null; // next {mode, color}
		let ramp = 1; // build-in of the current scene

		let dust = [];
		let beams = [];
		let speedLines = [];
		let bursts = [];
		let exhaust = [];
		let liftSparks = [];
		let liftRings = [];
		let stars = [];
		let comets = [];
		let branches = [];
		let expRings = [];
		let patches = [];

		const tintOf = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
		const tint = (a) => tintOf(cur.map(Math.round), a);

		function resize() {
			const rect = canvas.parentElement.getBoundingClientRect();
			width = rect.width;
			height = rect.height;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			if (!anchor.x) {
				anchor.x = width / 2;
				anchor.y = height / 2;
			}
			initDust();
		}

		function initDust() {
			const N = Math.round((width * height) / 34000);
			dust = Array.from({ length: N }, () => ({
				x: Math.random() * width,
				y: Math.random() * height,
				vx: (Math.random() - 0.5) * 0.18,
				vy: (Math.random() - 0.5) * 0.14,
				z: 0.3 + Math.random() * 0.7,
			}));
		}

		// ---- per-mode initializers ----------------------------------------
		function initModeState(m) {
			if (m === "accelerator") {
				const ORBITS = [
					{ tilt: -0.42, squash: 0.34, scale: 1.45 },
					{ tilt: 0.5, squash: 0.3, scale: 1.7 },
					{ tilt: 0.04, squash: 0.42, scale: 1.16 },
				];
				beams = Array.from({ length: 105 }, (_, i) => ({
					...ORBITS[i % 3],
					theta: Math.random() * Math.PI * 2,
					speed: 0.006 + Math.random() * 0.01,
					accel: 0.000045 + Math.random() * 0.00005,
					max: 0.03 + Math.random() * 0.012,
					size: 0.8 + Math.random() * 1.4,
				}));
				speedLines = [];
				bursts = [];
			} else if (m === "liftoff") {
				exhaust = [];
				liftSparks = [];
				liftRings = [];
				comets = [];
				stars = Array.from({ length: 70 }, () => ({
					x: Math.random() * width,
					y: Math.random() * height,
					v: 0.3 + Math.random() * 0.8,
					s: 0.6 + Math.random() * 1.4,
				}));
			} else {
				const DIRS = 14;
				branches = Array.from({ length: DIRS }, (_, i) => ({
					angle: (i / DIRS) * Math.PI * 2 + (Math.random() - 0.5) * 0.2,
					dist: anchor.R * 1.08,
					speed: 0.8 + Math.random() * 1.1,
					delay: Math.random() * 90,
					nodes: [],
					lastNode: 0,
				}));
				expRings = [];
				patches = [];
			}
		}
		initModeState(mode);

		// ---- drawing helpers ----------------------------------------------
		function orbitPoint(o, theta) {
			const R = anchor.R;
			const ox = Math.cos(theta) * R * o.scale;
			const oy = Math.sin(theta) * R * o.scale * o.squash;
			const ct = Math.cos(o.tilt);
			const st = Math.sin(o.tilt);
			return {
				x: anchor.x + ox * ct - oy * st,
				y: anchor.y + ox * st + oy * ct,
				front: Math.sin(theta) > 0,
			};
		}

		function drawDust() {
			for (const p of dust) {
				p.x += p.vx;
				p.y += p.vy;
				if (p.x < 0) p.x = width;
				if (p.x > width) p.x = 0;
				if (p.y < 0) p.y = height;
				if (p.y > height) p.y = 0;
				ctx.fillStyle = tint(0.14 * p.z);
				ctx.fillRect(p.x, p.y, 1.3, 1.3);
			}
		}

		function drawAccelerator(alpha) {
			// faint orbit guides
			ctx.lineWidth = 1;
			for (const o of [beams[0], beams[1], beams[2]]) {
				if (!o) break;
				ctx.strokeStyle = tint(0.08 * alpha);
				ctx.beginPath();
				for (let t = 0; t <= Math.PI * 2 + 0.05; t += 0.08) {
					const p = orbitPoint(o, t);
					if (t === 0) ctx.moveTo(p.x, p.y);
					else ctx.lineTo(p.x, p.y);
				}
				ctx.stroke();
			}

			// detector collisions where the orbits cross
			if (frame % 105 === 0 && beams[0]) {
				const at = orbitPoint(beams[0], 0.35);
				for (let i = 0; i < 14; i++) {
					const a = Math.random() * Math.PI * 2;
					const v = 1 + Math.random() * 2.4;
					bursts.push({
						x: at.x,
						y: at.y,
						vx: Math.cos(a) * v,
						vy: Math.sin(a) * v,
						life: 1,
					});
				}
			}
			for (let i = bursts.length - 1; i >= 0; i--) {
				const b2 = bursts[i];
				b2.x += b2.vx;
				b2.y += b2.vy;
				b2.life -= 0.03;
				if (b2.life <= 0) {
					bursts.splice(i, 1);
					continue;
				}
				ctx.fillStyle =
					b2.life > 0.6
						? `rgba(255,255,255,${b2.life * 0.8 * alpha})`
						: tint(b2.life * 0.6 * alpha);
				ctx.beginPath();
				ctx.arc(b2.x, b2.y, 1.5, 0, Math.PI * 2);
				ctx.fill();
			}

			for (const p of beams) {
				p.speed = Math.min(p.max, p.speed + p.accel);
				if (p.speed >= p.max && Math.random() < 0.004) {
					p.speed = 0.006 + Math.random() * 0.008;
				}
				p.theta += p.speed;
				const head = orbitPoint(p, p.theta);
				const tail = orbitPoint(p, p.theta - p.speed * 6);
				const energy = p.speed / p.max;
				const a = (head.front ? 0.7 : 0.2) * (0.35 + energy * 0.65) * alpha;
				ctx.strokeStyle = tint(a);
				ctx.lineWidth = p.size * (0.7 + energy);
				ctx.beginPath();
				ctx.moveTo(tail.x, tail.y);
				ctx.lineTo(head.x, head.y);
				ctx.stroke();
				if (energy > 0.85 && head.front) {
					ctx.fillStyle = `rgba(255,255,255,${0.55 * energy * alpha})`;
					ctx.beginPath();
					ctx.arc(head.x, head.y, 1.6, 0, Math.PI * 2);
					ctx.fill();
				}
			}

			// stray beams escaping across the whole section
			if (frame % 34 === 0) {
				const fromLeft = Math.random() < 0.5;
				speedLines.push({
					x: fromLeft ? -80 : width + 80,
					y: Math.random() * height,
					v: (fromLeft ? 1 : -1) * (16 + Math.random() * 10),
					len: 70 + Math.random() * 90,
				});
			}
			for (let i = speedLines.length - 1; i >= 0; i--) {
				const s = speedLines[i];
				s.x += s.v;
				if (s.x < -220 || s.x > width + 220) {
					speedLines.splice(i, 1);
					continue;
				}
				const grad = ctx.createLinearGradient(s.x - s.len, s.y, s.x, s.y);
				grad.addColorStop(0, tint(0));
				grad.addColorStop(1, tint(0.4 * alpha));
				ctx.strokeStyle = grad;
				ctx.lineWidth = 1.2;
				ctx.beginPath();
				ctx.moveTo(s.x - s.len * Math.sign(s.v), s.y);
				ctx.lineTo(s.x, s.y);
				ctx.stroke();
			}
		}

		function drawLiftoff(alpha) {
			// sinking starfield sells the whole section ascending
			for (const st of stars) {
				st.y += st.v;
				if (st.y > height) {
					st.y = -4;
					st.x = Math.random() * width;
				}
				ctx.fillStyle = tint(0.2 * st.s * alpha);
				ctx.fillRect(st.x, st.y, st.s, st.s * 2.4);
			}

			// the rocket leaves the stage the instant a transition starts, so
			// the engine must cut out with it: no flare, no fresh particles,
			// and whatever is airborne burns out fast
			const leaving = !!pending;

			if (!leaving) {
				// nozzle flare: flickering white-hot core right under the rocket
				const nozzleY = anchor.y + anchor.R * 0.66;
				const flicker = 0.5 + Math.random() * 0.2;
				const flare = ctx.createRadialGradient(
					anchor.x,
					nozzleY,
					0,
					anchor.x,
					nozzleY,
					40,
				);
				flare.addColorStop(0, `rgba(255,255,255,${flicker * alpha})`);
				flare.addColorStop(0.35, tint(0.45 * alpha));
				flare.addColorStop(1, tint(0));
				ctx.fillStyle = flare;
				ctx.beginPath();
				ctx.arc(anchor.x, nozzleY, 40, 0, Math.PI * 2);
				ctx.fill();

				for (let i = 0; i < 9; i++) {
					exhaust.push({
						x: anchor.x + (Math.random() - 0.5) * anchor.R * 0.4,
						y: anchor.y + anchor.R * 0.68,
						vx: (Math.random() - 0.5) * 1.1,
						vy: 2.2 + Math.random() * 2.6,
						life: 1,
					});
				}
			}
			for (let i = exhaust.length - 1; i >= 0; i--) {
				const p = exhaust[i];
				p.x += p.vx;
				p.vx += (Math.random() - 0.5) * 0.14; // turbulence
				p.y += p.vy;
				p.vy *= 0.988;
				p.life -= leaving ? 0.07 : 0.016;
				if (p.life <= 0) {
					exhaust.splice(i, 1);
					continue;
				}
				ctx.fillStyle =
					p.life > 0.72
						? `rgba(255,255,255,${(p.life - 0.5) * 1.1 * alpha})`
						: tint(p.life * 0.62 * alpha);
				ctx.beginPath();
				ctx.arc(p.x, p.y, 1.3 + (1 - p.life) * 3.4, 0, Math.PI * 2);
				ctx.fill();
			}

			if (frame % 3 === 0 && !leaving) {
				liftSparks.push({
					x: Math.random() * width,
					y: height * 0.55 + Math.random() * height * 0.45,
					vy: -(0.5 + Math.random() * 0.9),
					life: 1,
				});
			}
			for (let i = liftSparks.length - 1; i >= 0; i--) {
				const s = liftSparks[i];
				s.y += s.vy;
				s.life -= leaving ? 0.05 : 0.009;
				if (s.life <= 0) {
					liftSparks.splice(i, 1);
					continue;
				}
				ctx.fillStyle = tint(s.life * 0.35 * alpha);
				ctx.fillRect(s.x, s.y, 1.4, 4.5);
			}

			if (frame % 140 === 0 && !leaving)
				liftRings.push({ radius: anchor.R * 0.55, life: 1 });
			for (let i = liftRings.length - 1; i >= 0; i--) {
				const ring = liftRings[i];
				ring.radius += 2.4;
				ring.life -= leaving ? 0.06 : 0.016;
				if (ring.life <= 0) {
					liftRings.splice(i, 1);
					continue;
				}
				ctx.strokeStyle = tint(ring.life * 0.3 * alpha);
				ctx.lineWidth = 1.5;
				ctx.beginPath();
				ctx.ellipse(
					anchor.x,
					anchor.y + anchor.R * 0.85,
					ring.radius,
					ring.radius * 0.32,
					0,
					0,
					Math.PI * 2,
				);
				ctx.stroke();
			}

			// full-height comets punching upward
			if (frame % 150 === 0) {
				comets.push({
					x: width * (0.08 + Math.random() * 0.84),
					y: height + 30,
					v: 6 + Math.random() * 3,
				});
			}
			for (let i = comets.length - 1; i >= 0; i--) {
				const c = comets[i];
				c.y -= c.v;
				if (c.y < -90) {
					comets.splice(i, 1);
					continue;
				}
				const grad = ctx.createLinearGradient(c.x, c.y + 80, c.x, c.y);
				grad.addColorStop(0, tint(0));
				grad.addColorStop(1, tint(0.5 * alpha));
				ctx.strokeStyle = grad;
				ctx.lineWidth = 1.6;
				ctx.beginPath();
				ctx.moveTo(c.x, c.y + 80);
				ctx.lineTo(c.x, c.y);
				ctx.stroke();
				ctx.fillStyle = `rgba(255,255,255,${0.7 * alpha})`;
				ctx.beginPath();
				ctx.arc(c.x, c.y, 1.8, 0, Math.PI * 2);
				ctx.fill();
			}
		}

		function drawExpansion(alpha) {
			const maxDist = Math.max(width, height) * 0.62;

			if (frame % 130 === 0) expRings.push({ radius: anchor.R * 1.05, life: 1 });
			for (let i = expRings.length - 1; i >= 0; i--) {
				const ring = expRings[i];
				ring.radius += 1.9;
				ring.life -= 0.011;
				if (ring.life <= 0) {
					expRings.splice(i, 1);
					continue;
				}
				ctx.strokeStyle = tint(ring.life * 0.2 * alpha);
				ctx.lineWidth = 1.2;
				ctx.beginPath();
				ctx.arc(anchor.x, anchor.y, ring.radius, 0, Math.PI * 2);
				ctx.stroke();
			}

			// glow footholds conquering the far corners of the section
			if (frame % 120 === 0) {
				patches.push({
					x: Math.random() * width,
					y: Math.random() * height,
					life: 1,
				});
			}
			for (let i = patches.length - 1; i >= 0; i--) {
				const pa = patches[i];
				pa.life -= 0.008;
				if (pa.life <= 0) {
					patches.splice(i, 1);
					continue;
				}
				const glow = ctx.createRadialGradient(pa.x, pa.y, 0, pa.x, pa.y, 46);
				glow.addColorStop(0, tint(0.14 * pa.life * alpha));
				glow.addColorStop(1, tint(0));
				ctx.fillStyle = glow;
				ctx.beginPath();
				ctx.arc(pa.x, pa.y, 46, 0, Math.PI * 2);
				ctx.fill();
			}

			for (const br of branches) {
				if (br.delay > 0) {
					br.delay--;
					continue;
				}
				br.dist += br.speed;
				if (br.dist - br.lastNode > 36) {
					br.nodes.push(br.dist);
					br.lastNode = br.dist;
				}
				if (br.dist > maxDist) {
					br.dist = anchor.R * 1.08;
					br.lastNode = 0;
					br.nodes = [];
					br.delay = 40 + Math.random() * 80;
					br.angle += (Math.random() - 0.5) * 0.25;
					continue;
				}
				const ax = Math.cos(br.angle);
				const ay = Math.sin(br.angle);
				const x0 = anchor.x + ax * anchor.R * 1.08;
				const y0 = anchor.y + ay * anchor.R * 1.08;
				const x1 = anchor.x + ax * br.dist;
				const y1 = anchor.y + ay * br.dist;
				// consolidated near the globe, fading out along the newly
				// conquered stretch
				const lg = ctx.createLinearGradient(x0, y0, x1, y1);
				lg.addColorStop(0, tint(0.26 * alpha));
				lg.addColorStop(0.65, tint(0.13 * alpha));
				lg.addColorStop(1, tint(0.02 * alpha));
				ctx.strokeStyle = lg;
				ctx.lineWidth = 1;
				ctx.beginPath();
				ctx.moveTo(x0, y0);
				ctx.lineTo(x1, y1);
				ctx.stroke();
				ctx.fillStyle = `rgba(255,255,255,${0.35 * alpha})`;
				ctx.beginPath();
				ctx.arc(x1, y1, 1.6, 0, Math.PI * 2);
				ctx.fill();
				for (const d of br.nodes) {
					const settle = Math.min(1, (br.dist - d) / 60);
					ctx.fillStyle = tint((0.22 + settle * 0.3) * alpha);
					ctx.beginPath();
					ctx.arc(anchor.x + ax * d, anchor.y + ay * d, 2.1, 0, Math.PI * 2);
					ctx.fill();
				}
			}
		}

		function handover() {
			mode = pending.mode;
			color = pending.color;
			initModeState(mode);
			ramp = 0;
			pending = null;
		}

		/* each unit is announced by its own signature transition:
		   warp (accelerator), launch (liftoff) or radial blast (expansion) */
		function drawWave() {
			if (!wave) return;
			wave.flash *= 0.9;
			if (wave.flash > 0.02) {
				ctx.fillStyle = tintOf(wave.color, wave.flash * 0.1);
				ctx.fillRect(0, 0, width, height);
			}

			if (wave.style === "warp") {
				// fan of beams accelerating from the button across the section
				let front = 0;
				for (const s of wave.streaks) {
					s.v *= 1.055;
					s.d += s.v;
					front = Math.max(front, s.d);
					const hx = wave.x + Math.cos(s.a) * s.d;
					const hy = wave.y + Math.sin(s.a) * s.d;
					const tx = wave.x + Math.cos(s.a) * Math.max(0, s.d - s.len);
					const ty = wave.y + Math.sin(s.a) * Math.max(0, s.d - s.len);
					const lg = ctx.createLinearGradient(tx, ty, hx, hy);
					lg.addColorStop(0, tintOf(wave.color, 0));
					lg.addColorStop(1, tintOf(wave.color, 0.55));
					ctx.strokeStyle = lg;
					ctx.lineWidth = s.w;
					ctx.beginPath();
					ctx.moveTo(tx, ty);
					ctx.lineTo(hx, hy);
					ctx.stroke();
					ctx.fillStyle = `rgba(255,255,255,0.6)`;
					ctx.fillRect(hx - 1, hy - 1, 2, 2);
				}
				wave.progress = front / wave.cover;
				if (pending && front > wave.cover) handover();
				if (front > Math.hypot(width, height) * 1.15) wave = null;
			} else if (wave.style === "launch") {
				// eruption at the button + pressure band sweeping vertically
				wave.band += 8 + wave.band * 0.05;
				for (let i = 0; i < 6; i++) {
					wave.jet.push({
						x: wave.x + (Math.random() - 0.5) * 16,
						y: wave.y,
						vx: (Math.random() - 0.5) * 1.6,
						vy: -(2.6 + Math.random() * 3.4),
						life: 1,
					});
				}
				for (let i = wave.jet.length - 1; i >= 0; i--) {
					const j = wave.jet[i];
					j.x += j.vx;
					j.y += j.vy;
					j.vy += 0.05;
					j.life -= 0.02;
					if (j.life <= 0) {
						wave.jet.splice(i, 1);
						continue;
					}
					ctx.fillStyle =
						j.life > 0.7
							? `rgba(255,255,255,${j.life * 0.8})`
							: tintOf(wave.color, j.life * 0.6);
					ctx.beginPath();
					ctx.arc(j.x, j.y, 1.6, 0, Math.PI * 2);
					ctx.fill();
				}
				const topY = wave.y - wave.band;
				const botY = wave.y + wave.band;
				for (const yy of [topY, botY]) {
					if (yy < -20 || yy > height + 20) continue;
					ctx.strokeStyle = tintOf(wave.color, 0.4);
					ctx.lineWidth = 2.2;
					ctx.beginPath();
					ctx.moveTo(0, yy);
					ctx.lineTo(width, yy);
					ctx.stroke();
					for (let i = 0; i < 14; i++) {
						ctx.fillStyle = tintOf(wave.color, 0.5 * Math.random());
						ctx.fillRect(
							Math.random() * width,
							yy + (Math.random() - 0.5) * 18,
							1.6,
							3,
						);
					}
				}
				const need = Math.abs(anchor.y - wave.y) + anchor.R * 0.9;
				wave.progress = wave.band / need;
				if (pending && wave.band > need) handover();
				if (topY < -60 && botY > height + 60) wave = null;
			} else {
				// radial blast
				wave.r += 10 + wave.r * 0.045;
				ctx.strokeStyle = tintOf(wave.color, 0.4);
				ctx.lineWidth = 7;
				ctx.beginPath();
				ctx.arc(wave.x, wave.y, wave.r, 0, Math.PI * 2);
				ctx.stroke();
				ctx.strokeStyle = `rgba(255,255,255,0.5)`;
				ctx.lineWidth = 1.4;
				ctx.beginPath();
				ctx.arc(wave.x, wave.y, wave.r + 4, 0, Math.PI * 2);
				ctx.stroke();
				for (let i = 0; i < 26; i++) {
					const a = Math.random() * Math.PI * 2;
					const rr = wave.r + (Math.random() - 0.5) * 26;
					ctx.fillStyle = tintOf(wave.color, 0.5 * Math.random());
					ctx.fillRect(
						wave.x + Math.cos(a) * rr,
						wave.y + Math.sin(a) * rr,
						1.6,
						1.6,
					);
				}
				wave.progress = wave.r / wave.cover;
				if (pending && wave.r > wave.cover) handover();
				const maxR = Math.hypot(
					Math.max(wave.x, width - wave.x),
					Math.max(wave.y, height - wave.y),
				);
				if (wave.r > maxR) wave = null;
			}
		}

		function draw() {
			frame++;
			ctx.clearRect(0, 0, width, height);
			ctx.globalCompositeOperation = "lighter";

			// display color chases the incoming unit's accent
			const target = pending ? pending.color : color;
			for (let c = 0; c < 3; c++) cur[c] += (target[c] - cur[c]) * 0.05;

			drawDust();

			ramp = Math.min(1, ramp + 0.018);
			// old scene dims as the transition devours it
			const sceneAlpha = pending && wave
				? Math.max(0.05, 1 - (wave.progress || 0) * 1.1) * ramp
				: ramp;

			if (mode === "accelerator") drawAccelerator(sceneAlpha);
			else if (mode === "liftoff") drawLiftoff(sceneAlpha);
			else drawExpansion(sceneAlpha);

			drawWave();
			ctx.globalCompositeOperation = "source-over";
		}

		function loop() {
			rafId = requestAnimationFrame(loop);
			if (inView) draw();
		}

		function start() {
			if (running || reducedMotion) return;
			running = true;
			rafId = requestAnimationFrame(loop);
		}

		function stop() {
			running = false;
			if (rafId) cancelAnimationFrame(rafId);
			rafId = null;
		}

		const io =
			typeof IntersectionObserver !== "undefined"
				? new IntersectionObserver(([entry]) => {
						inView = entry.isIntersecting;
					})
				: null;
		if (io) io.observe(canvas);

		function onVisibility() {
			if (document.hidden) stop();
			else start();
		}

		function onResize() {
			resize();
			initModeState(mode);
			if (reducedMotion) {
				for (let i = 0; i < 240; i++) draw();
			}
		}

		window.addEventListener("resize", onResize);
		document.addEventListener("visibilitychange", onVisibility);

		// re-measure whenever the section's box actually changes (fonts, late
		// layout, dev fouc-guard) — a mount-time measurement can be stale
		let lastW = 0;
		let lastH = 0;
		const ro =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => {
						const rect = canvas.parentElement.getBoundingClientRect();
						if (
							Math.abs(rect.width - lastW) > 1 ||
							Math.abs(rect.height - lastH) > 1
						) {
							lastW = rect.width;
							lastH = rect.height;
							onResize();
						}
					})
				: null;
		if (ro) ro.observe(canvas.parentElement);

		engineRef.current = {
			setAnchor(x, y, r) {
				anchor.x = x;
				anchor.y = y;
				if (r) anchor.R = r;
			},
			transition(next) {
				const nextColor = hexToRgb(next.accent);
				if (next.mode === mode && !pending) return;
				pending = { mode: next.mode, color: nextColor };
				const cover =
					Math.hypot(anchor.x - next.x, anchor.y - next.y) + anchor.R * 1.3;
				const style =
					next.mode === "accelerator"
						? "warp"
						: next.mode === "liftoff"
							? "launch"
							: "radial";
				wave = {
					style,
					x: next.x,
					y: next.y,
					r: 0,
					band: 0,
					jet: [],
					progress: 0,
					flash: 1,
					color: nextColor,
					cover,
				};
				if (style === "warp") {
					const baseAngle = Math.atan2(anchor.y - next.y, anchor.x - next.x);
					wave.streaks = Array.from({ length: 54 }, () => ({
						a: baseAngle + (Math.random() - 0.5) * 1.1,
						v: 3 + Math.random() * 2.5,
						d: 0,
						len: 50 + Math.random() * 80,
						w: 1 + Math.random() * 1.6,
					}));
				}
				if (reducedMotion) {
					// no cinematics: cut straight to the next scene
					mode = next.mode;
					color = nextColor;
					wave = null;
					pending = null;
					initModeState(mode);
					for (let i = 0; i < 240; i++) draw();
				}
			},
		};

		resize();
		initModeState(mode);

		if (reducedMotion) {
			for (let i = 0; i < 240; i++) draw();
		} else {
			start();
		}

		return () => {
			stop();
			if (io) io.disconnect();
			if (ro) ro.disconnect();
			window.removeEventListener("resize", onResize);
			document.removeEventListener("visibilitychange", onVisibility);
			engineRef.current = null;
		};
		// the engine lives for the component's whole life; transitions arrive
		// imperatively through the ref API
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return <Canvas ref={canvasRef} aria-hidden="true" />;
});

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
`;

export default DivisionCinema;
